// ===================================================================
// Inscritos (modo prova) — visão operacional de pagamento e atendimento especial
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortApplicationsForDisplay,
  sortColumnIndicator,
} from '../../utils/adminTableSort';
import {
  getPaymentStatusLabel,
  getSpecialAttendanceStatusLabel,
  getStatusBadgeStyle,
} from '../../constants/statusTranslations';
import { useNotifications } from '../../contexts/NotificationContext';

const paymentKeyForApp = (app) => {
  if (!app) return 'pending';
  if (app.payment_status === 'paid') return 'paid';
  if (app.exemption_status) {
    switch (app.exemption_status) {
      case 'approved':
        return 'exempted';
      case 'rejected':
        return 'exemption_rejected';
      case 'returned':
        return 'exemption_returned';
      case 'appealed':
        return 'exemption_appealed';
      case 'appeal_approved':
        return 'exemption_appeal_approved';
      case 'appeal_rejected':
        return 'exemption_appeal_rejected';
      case 'pending': {
        const hasEx = app.exemption_analyst_id || app.exemption_analyst_name;
        if (!hasEx) return 'pending_exemption_not_assigned';
        return app.exemption_request_status === 'analyzing' ? 'pending_exemption' : 'pending_exemption_assigned';
      }
      default:
        break;
    }
  }
  if (app.payment_status) return app.payment_status;
  return 'pending';
};

const isAppEligibleForAllocation = (app) => {
  const key = paymentKeyForApp(app);
  return key === 'paid' || key === 'exempted' || key === 'exemption_appeal_approved';
};

const specialAttendancePayOk = (app) => {
  const payKey = paymentKeyForApp(app);
  return payKey === 'paid' || payKey === 'exempted' || payKey === 'exemption_appeal_approved';
};

const specialAttendanceColumnLabel = (app) => {
  if (!app.special_attendance_request_id) return '—';
  const st = (app.special_attendance_request_status || 'pending').toLowerCase();
  const base = getSpecialAttendanceStatusLabel(st);
  if (!specialAttendancePayOk(app)) return `${base} (aguarda pagamento ou isenção)`;
  return base;
};

/** Cores do badge — recursos espelham as chaves de recurso de isenção (mesma paleta). */
const specialAttendanceBadgeKey = (app) => {
  if (!specialAttendancePayOk(app)) return 'pending';
  const st = (app.special_attendance_request_status || 'pending').toLowerCase();
  if (st === 'approved') return 'approved';
  if (st === 'rejected') return 'rejected';
  if (st === 'analyzing') return 'analyzing';
  if (st === 'returned') return 'returned';
  if (st === 'appealed') return 'exemption_appealed';
  if (st === 'appeal_approved') return 'exemption_appeal_approved';
  if (st === 'appeal_rejected') return 'exemption_appeal_rejected';
  return 'pending';
};

export const ExamEnrolledList = React.memo(() => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const { addNotification } = useNotifications();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());
  const [examSheetsExportBusy, setExamSheetsExportBusy] = useState(false);
  const [examVenues, setExamVenues] = useState([]);
  const [exportVenueId, setExportVenueId] = useState('');
  const [exportRoomId, setExportRoomId] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterAllocationStatus, setFilterAllocationStatus] = useState('');
  const [distributionPreview, setDistributionPreview] = useState(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const load = useCallback(async () => {
    const { id: pid, empty, invalid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty || invalid || pid == null) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiClient.getApplications({ processId: pid });
      const list = Array.isArray(data) ? data : [];
      const visible = list.filter((a) => !a.excluded_at);
      setRows(visible);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProcessId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setTableSort(initialAdminTableSort());
  }, [selectedProcessId]);

  const sortedRows = useMemo(() => sortApplicationsForDisplay(rows, tableSort), [rows, tableSort]);
  const appAllocationMap = useMemo(() => (distributionPreview?.app_allocations || {}), [distributionPreview]);
  const cityOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.course_campus) set.add(String(r.course_campus).trim());
    });
    examVenues.forEach((v) => {
      if (v.city_state) set.add(String(v.city_state).trim());
    });
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows, examVenues]);
  const getAllocationInfo = useCallback((app) => {
    const raw = appAllocationMap[String(app.id)];
    if (raw) return raw;
    if (!isAppEligibleForAllocation(app)) {
      return {
        status: 'pending',
        status_reason: 'not_eligible_yet',
        status_label: 'Aguardando pagamento/isenção',
        city: app.course_campus || null,
        venue_name: null,
        room_name: null,
      };
    }
    return {
      status: 'pending',
      status_reason: 'missing_exam_venue_mapping',
      status_label: 'Pendente: sem local na cidade',
      city: app.course_campus || null,
      venue_name: null,
      room_name: null,
    };
  }, [appAllocationMap]);
  const filteredRows = useMemo(() => {
    return sortedRows.filter((app) => {
      const info = getAllocationInfo(app);
      if (exportVenueId && String(info.venue_id || '') !== String(exportVenueId)) return false;
      if (exportRoomId && String(info.room_id || '') !== String(exportRoomId)) return false;
      if (filterCity) {
        const city = String(info.city || app.course_campus || '').trim();
        if (city !== String(filterCity).trim()) return false;
      }
      if (filterAllocationStatus) {
        if (filterAllocationStatus === 'allocated' && info.status !== 'allocated') return false;
        if (filterAllocationStatus === 'missing_exam_venue_mapping' && info.status_reason !== 'missing_exam_venue_mapping') return false;
        if (filterAllocationStatus === 'capacity_exhausted' && info.status_reason !== 'capacity_exhausted') return false;
        if (filterAllocationStatus === 'missing_location_choice' && info.status_reason !== 'missing_location_choice') return false;
      }
      return true;
    });
  }, [sortedRows, getAllocationInfo, exportVenueId, exportRoomId, filterCity, filterAllocationStatus]);

  const provaProcesses = useMemo(
    () => (processes || []).filter((p) => p.selection_mode === 'prova'),
    [processes]
  );

  const selectedProcessMeta = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return null;
    return (processes || []).find((p) => Number(p.id) === Number(pid)) || null;
  }, [processes, selectedProcessId]);

  const hasExamSheets =
    selectedProcessMeta &&
    (Boolean(selectedProcessMeta.exam_uses_answer_sheet) || Boolean(selectedProcessMeta.exam_uses_essay));

  const handleExportExamSheets = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return;
    setExamSheetsExportBusy(true);
    try {
      const { blob, filename } = await apiClient.exportProcessExamSheetsZip(pid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addNotification('Download', 'ZIP com as folhas foi gerado.', 'success');
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao exportar folhas', 'error');
    } finally {
      setExamSheetsExportBusy(false);
    }
  }, [selectedProcessId, addNotification]);

  const handleExportExamSheetsByRoom = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return;
    setExamSheetsExportBusy(true);
    try {
      let preview = distributionPreview;
      if (!preview?.venues?.length) {
        try {
          preview = await apiClient.getExamSheetsDistributionPreview(pid);
        } catch (err) {
          addNotification('Erro', err.message || 'Não foi possível carregar a distribuição por sala.', 'error');
          return;
        }
      }
      const venuesList = (preview?.venues || []).filter((v) => !exportVenueId || String(v.id) === String(exportVenueId));
      const tasks = [];
      for (const v of venuesList) {
        for (const r of v.rooms || []) {
          if (Number(r.assigned || 0) > 0) tasks.push({ roomId: r.id });
        }
      }
      if (tasks.length === 0) {
        addNotification('Atenção', 'Nenhuma sala com inscritos alocados. Verifique locais/salas e a distribuição.', 'warning');
        return;
      }
      let downloaded = 0;
      for (let i = 0; i < tasks.length; i += 1) {
        const { roomId } = tasks[i];
        const { blob, filename } = await apiClient.exportProcessExamSheetsZip(pid, {
          mode: 'all',
          roomId,
          venueId: exportVenueId || undefined,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        downloaded += 1;
        if (i < tasks.length - 1) {
          await new Promise((res) => setTimeout(res, 450));
        }
      }
      addNotification(
        'Download',
        `${downloaded} arquivo(s) ZIP (um por sala, sem ZIP dentro de ZIP). O navegador pode pedir permissão para vários downloads.`,
        'success'
      );
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao exportar por sala', 'error');
    } finally {
      setExamSheetsExportBusy(false);
    }
  }, [selectedProcessId, exportVenueId, distributionPreview, addNotification]);

  const handleExportExamSheetsFiltered = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return;
    setExamSheetsExportBusy(true);
    try {
      const { blob, filename } = await apiClient.exportProcessExamSheetsZip(pid, {
        mode: 'all',
        venueId: exportVenueId || undefined,
        roomId: exportRoomId || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addNotification('Download', 'ZIP filtrado por local/sala foi gerado.', 'success');
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao exportar filtrado', 'error');
    } finally {
      setExamSheetsExportBusy(false);
    }
  }, [selectedProcessId, exportVenueId, exportRoomId, addNotification]);

  const handleExportPendingCsv = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return;
    setExamSheetsExportBusy(true);
    try {
      const { blob, filename } = await apiClient.exportExamSheetsPendingCsv(pid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      addNotification('Download', 'CSV de pendentes exportado.', 'success');
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao exportar pendentes', 'error');
    } finally {
      setExamSheetsExportBusy(false);
    }
  }, [selectedProcessId, addNotification]);

  const handleOpenLocais = useCallback(() => {
    window.dispatchEvent(new CustomEvent('ingressa:navigate', { detail: { page: 'processes' } }));
    addNotification('Locais de prova', 'Você foi direcionado para Processos. Abra o processo e use o botão Locais.', 'info');
  }, [addNotification]);

  useEffect(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (!hasExamSheets || pid == null) {
      setExamVenues([]);
      setExportVenueId('');
      setExportRoomId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await apiClient.getExamVenues(pid);
        if (!cancelled) setExamVenues(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setExamVenues([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProcessId, hasExamSheets]);

  const roomOptions = useMemo(() => {
    if (!exportVenueId) return [];
    const v = examVenues.find((x) => String(x.id) === String(exportVenueId));
    return Array.isArray(v?.rooms) ? v.rooms : [];
  }, [examVenues, exportVenueId]);

  const loadDistributionPreview = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (!hasExamSheets || pid == null) {
      setDistributionPreview(null);
      return;
    }
    setDistributionLoading(true);
    try {
      const data = await apiClient.getExamSheetsDistributionPreview(pid);
      setDistributionPreview(data || null);
    } catch {
      setDistributionPreview(null);
    } finally {
      setDistributionLoading(false);
    }
  }, [selectedProcessId, hasExamSheets]);

  useEffect(() => {
    loadDistributionPreview();
  }, [loadDistributionPreview]);

  if (loading && rows.length === 0) {
    return <LoadingSpinner text="Carregando inscritos…" />;
  }

  return (
    <div style={{ ...styles.contentContainer, padding: '16px' }}>
      <h2 style={styles.cardTitle}>Inscritos</h2>
      <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px', maxWidth: '820px', lineHeight: 1.5 }}>
        Visão geral das inscrições do processo por <strong>prova</strong>: pagamento, atendimento especial e alocação de
        local/sala. Notas e folhas escaneadas ficam em <strong>Prova objetiva</strong> e <strong>Redação</strong>. Quem não
        pediu atendimento especial aparece com &quot;—&quot; nessa coluna.
      </p>

      <div style={{ ...styles.card, marginBottom: '16px' }}>
        <label style={styles.label}>Processo seletivo (prova)</label>
        <select value={selectedProcessId} onChange={setSelectedProcessId} style={{ ...styles.select, maxWidth: '420px' }}>
          <option value="">Selecione o processo</option>
          {provaProcesses.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>
        {hasExamSheets && (
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)} style={{ ...styles.select, maxWidth: 220 }}>
              <option value="">Todas as cidades</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={examSheetsExportBusy}
              onClick={handleExportExamSheets}
              style={{ ...styles.button, ...styles.buttonSecondary, opacity: examSheetsExportBusy ? 0.7 : 1 }}
            >
              {examSheetsExportBusy ? 'Gerando…' : 'Baixar ZIP geral'}
            </button>
            <select
              value={exportVenueId}
              onChange={(e) => {
                setExportVenueId(e.target.value);
                setExportRoomId('');
              }}
              style={{ ...styles.select, maxWidth: 260 }}
            >
              <option value="">Todos os locais</option>
              {examVenues.map((v) => (
                <option key={v.id} value={String(v.id)}>
                  {v.name}
                </option>
              ))}
            </select>
            <select
              value={exportRoomId}
              onChange={(e) => setExportRoomId(e.target.value)}
              style={{ ...styles.select, maxWidth: 220 }}
              disabled={!exportVenueId}
            >
              <option value="">Todas as salas</option>
              {roomOptions.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.name} ({r.capacity})
                </option>
              ))}
            </select>
            <select
              value={filterAllocationStatus}
              onChange={(e) => setFilterAllocationStatus(e.target.value)}
              style={{ ...styles.select, maxWidth: 260 }}
            >
              <option value="">Todos os status de alocação</option>
              <option value="allocated">Alocado</option>
              <option value="missing_exam_venue_mapping">Pendente: sem local na cidade</option>
              <option value="capacity_exhausted">Pendente: sem vaga</option>
              <option value="missing_location_choice">Pendente: candidato não escolheu local</option>
            </select>
            <button
              type="button"
              disabled={examSheetsExportBusy}
              onClick={handleExportExamSheetsFiltered}
              style={{ ...styles.button, ...styles.buttonSecondary, opacity: examSheetsExportBusy ? 0.7 : 1 }}
            >
              {examSheetsExportBusy ? 'Gerando…' : 'Baixar ZIP filtrado'}
            </button>
            <button
              type="button"
              disabled={examSheetsExportBusy}
              onClick={handleExportExamSheetsByRoom}
              style={{ ...styles.button, ...styles.buttonPrimary, opacity: examSheetsExportBusy ? 0.7 : 1 }}
            >
              {examSheetsExportBusy ? 'Gerando…' : 'Baixar ZIP por sala'}
            </button>
            <span style={{ fontSize: 12, color: '#6B7280', maxWidth: 420, lineHeight: 1.4 }}>
              Inclui gabarito e/ou redação conforme o processo (apenas inscrições elegíveis: pagas ou isentas). O botão{' '}
              <strong>Baixar ZIP por sala</strong> gera <strong>um arquivo ZIP por sala</strong> (downloads em sequência; o filtro de local limita às salas daquele polo).
            </span>
          </div>
        )}
        {hasExamSheets && (
          <div style={{ marginTop: 12, padding: 10, border: '1px solid #E5E7EB', borderRadius: 8, background: '#FCFCFD' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <strong style={{ fontSize: 13 }}>Pré-visualização da distribuição por sala</strong>
              <button
                type="button"
                style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 12, padding: '6px 10px' }}
                onClick={loadDistributionPreview}
              >
                Atualizar
              </button>
            </div>
            {distributionLoading && <div style={{ fontSize: 12, color: '#6B7280' }}>Calculando distribuição…</div>}
            {!distributionLoading && distributionPreview && (
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                <div>
                  Elegíveis: <strong>{distributionPreview.total_eligible ?? 0}</strong> | Capacidade: <strong>{distributionPreview.total_capacity ?? 0}</strong> | Não alocados: <strong>{distributionPreview.unallocated ?? 0}</strong> | Cidades sem local: <strong>{distributionPreview.cities_without_venue_count ?? 0}</strong>
                </div>
                <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 999, padding: '2px 8px' }}>
                    Sem local escolhido: <strong>{distributionPreview?.unallocated_reasons?.missing_location_choice ?? 0}</strong>
                  </span>
                  <span style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 999, padding: '2px 8px' }}>
                    Sem local cadastrado/mapeado: <strong>{distributionPreview?.unallocated_reasons?.missing_exam_venue_mapping ?? 0}</strong>
                  </span>
                  <span style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 999, padding: '2px 8px' }}>
                    Lotação esgotada: <strong>{distributionPreview?.unallocated_reasons?.capacity_exhausted ?? 0}</strong>
                  </span>
                </div>
                <div style={{ marginTop: 8, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#F3F4F6' }}>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'left' }}>Cidade</th>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'left' }}>Local</th>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'left' }}>Sala</th>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Capacidade</th>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Alocados</th>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Ocupação</th>
                        <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Pendentes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(distributionPreview.distribution_rows || []).map((r, idx) => (
                        <tr key={`${r.city || 'x'}-${r.venue_name || 'x'}-${r.room_name || 'x'}-${idx}`}>
                          <td style={styles.tableCell}>{r.city || '---'}</td>
                          <td style={styles.tableCell}>{r.venue_name || '---'}</td>
                          <td style={styles.tableCell}>{r.room_name || '---'}</td>
                          <td style={{ ...styles.tableCell, textAlign: 'center' }}>{r.capacity ?? 0}</td>
                          <td style={{ ...styles.tableCell, textAlign: 'center' }}>{r.allocated ?? 0}</td>
                          <td style={{ ...styles.tableCell, textAlign: 'center' }}>{r.occupancy_pct == null ? '---' : `${Number(r.occupancy_pct).toFixed(0)}%`}</td>
                          <td style={{ ...styles.tableCell, textAlign: 'center' }}>{r.pending ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 12, padding: '6px 10px' }} onClick={loadDistributionPreview}>Recalcular distribuição</button>
                  <button type="button" style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 12, padding: '6px 10px' }} onClick={handleExportPendingCsv}>Exportar pendentes CSV</button>
                  <button type="button" style={{ ...styles.button, ...styles.buttonSecondary, fontSize: 12, padding: '6px 10px' }} onClick={handleOpenLocais}>Abrir Locais</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ background: '#F3F4F6' }}>
              <th
                role="columnheader"
                aria-sort={tableSort.key === 'id' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                style={{
                  ...styles.tableHeaderCell,
                  fontSize: '9px',
                  padding: '8px 6px',
                  width: '45px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => setTableSort((p) => nextAdminTableSort(p, 'id'))}
                title="Ordenar por número da inscrição"
              >
                Nº{sortColumnIndicator(tableSort, 'id')}
              </th>
              <th
                role="columnheader"
                aria-sort={tableSort.key === 'candidate' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                style={{
                  ...styles.tableHeaderCell,
                  fontSize: '10px',
                  padding: '8px 10px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
                title="Ordenar por nome do candidato"
              >
                Candidato{sortColumnIndicator(tableSort, 'candidate')}
              </th>
              <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px', textAlign: 'left' }}>Curso</th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '110px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>
                Cidade do curso
              </th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '110px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>Local prova</th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '90px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>Sala</th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '130px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>Status alocação</th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '48px', fontSize: '10px', padding: '8px 8px', textAlign: 'center' }}>
                Cota
              </th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '90px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>
                Pagamento
              </th>
              <th style={{ ...styles.tableHeaderCell, minWidth: '120px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>
                Atend. especial
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((app) => {
              const alloc = getAllocationInfo(app);
              return (<tr key={app.id} style={styles.tableRow}>
                <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 6px', fontWeight: '600', color: '#374151' }}>
                  #{app.id}
                </td>
                <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px', textAlign: 'left' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>{app.candidate_name || '—'}</div>
                    <div style={{ fontSize: '9px', color: '#6B7280' }}>CPF: {app.candidate_cpf || '—'}</div>
                  </div>
                </td>
                <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px', textAlign: 'left' }}>{app.course_name || '—'}</td>
                <td
                  style={{
                    ...styles.tableCell,
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    minWidth: '110px',
                    padding: '8px 10px',
                    fontSize: '10px',
                    color: '#374151',
                  }}
                >
                  {app.course_campus || '—'}
                </td>
                <td style={{ ...styles.tableCell, textAlign: 'center', fontSize: '10px' }}>{alloc.venue_name || '—'}</td>
                <td style={{ ...styles.tableCell, textAlign: 'center', fontSize: '10px' }}>{alloc.room_name || '—'}</td>
                <td style={{ ...styles.tableCell, textAlign: 'center', fontSize: '10px' }}>
                  <span style={{ ...styles.tableCellBadge, fontSize: '9px', padding: '4px 8px', background: alloc.status === 'allocated' ? '#DCFCE7' : '#FEE2E2', color: alloc.status === 'allocated' ? '#166534' : '#991B1B', border: `1px solid ${alloc.status === 'allocated' ? '#86EFAC' : '#FCA5A5'}` }}>
                    {alloc.status_label || 'Pendente'}
                  </span>
                </td>
                <td
                  style={{
                    ...styles.tableCell,
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    padding: '8px 8px',
                    fontSize: '10px',
                    fontWeight: '600',
                  }}
                >
                  {app.quota_type || 'AC'}
                </td>
                <td
                  style={{
                    ...styles.tableCell,
                    verticalAlign: 'middle',
                    minWidth: '90px',
                    whiteSpace: 'normal',
                    padding: '8px 10px',
                    textAlign: 'center',
                  }}
                >
                  <span
                    style={{
                      ...getStatusBadgeStyle(paymentKeyForApp(app)),
                      ...styles.tableCellBadge,
                      fontSize: '9px',
                      padding: '4px 8px',
                      display: 'inline-block',
                      maxWidth: '100%',
                      textAlign: 'center',
                    }}
                    title={getPaymentStatusLabel(paymentKeyForApp(app))}
                  >
                    {getPaymentStatusLabel(paymentKeyForApp(app))}
                  </span>
                </td>
                <td
                  style={{
                    ...styles.tableCell,
                    verticalAlign: 'middle',
                    minWidth: '120px',
                    whiteSpace: 'normal',
                    padding: '8px 10px',
                    textAlign: 'center',
                  }}
                >
                  {app.special_attendance_request_id ? (
                    <span
                      style={{
                        ...getStatusBadgeStyle(specialAttendanceBadgeKey(app)),
                        ...styles.tableCellBadge,
                        fontSize: '9px',
                        padding: '4px 8px',
                        display: 'inline-block',
                        maxWidth: '100%',
                        textAlign: 'center',
                        lineHeight: 1.35,
                      }}
                      title={specialAttendanceColumnLabel(app)}
                    >
                      {specialAttendanceColumnLabel(app)}
                    </span>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>—</span>
                  )}
                </td>
              </tr>);
            })}
          </tbody>
        </table>
        {filteredRows.length === 0 && (
          <p style={{ marginTop: 12, color: '#6B7280' }}>Selecione um processo por prova com inscrições para listar.</p>
        )}
      </div>
    </div>
  );
});
